import React, { useMemo, useState } from 'react'
import { Box, Button, Link, Typography } from '@mui/material'
import { PortableText } from '@portabletext/react'
import { graphql, useStaticQuery } from 'gatsby'
import { DemocracyEngineDonationInput } from '../types/democracy-engine'
import { useDemocracyEngineDonation } from '../hooks/use-democracy-engine'
import { HBox, VBox, WhiteBox } from '../elements/basic-elements'
import InputComponent from './input'
import LoadingOverlay from './loading-overlay'
import PrimaryButton from './buttons/primary-button'
import CheckmarkIcon from '../images/icons/checkmark-icon'
import CenteredModal from './modals/centered-modal'
import { blockSerializers } from '../templates/portable-text'

const initialState = {
  donor_first_name: '',
  donor_last_name: '',
  donor_email: '',
  donor_phone: '',
  donor_address1: '',
  donor_city: '',
  donor_state: '',
  donor_zip: '',
  donor_country_code: 'US',
  amount: '',
  memo: '',
  cc_number: '',
  cc_verification_value: '',
  cc_month: '',
  cc_year: '',
  cc_zip: '',
  cc_first_name: '',
  cc_last_name: ''
}

type DonationFormState = typeof initialState

export const DemocracyEngineDonationForm = ({
  organization,
  recipientId,
  onClose
}: {
  organization: Queries.SanityOrganization
  recipientId: string
  actionId?: string
  onClose: () => void
}) => {
  const {
    submitBrowserDonation,
    submitting,
    submissionError,
    error: loadError
  } = useDemocracyEngineDonation()
  const [state, setState] = useState<DonationFormState>(initialState)
  const [successMessage, setSuccessMessage] = useState<string>()
  const [failureMessage, setFailureMessage] = useState<string>()
  const [dismissedError, setDismissedError] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const isDevelopment = process.env.NODE_ENV === 'development'

  const demoDefaults: DonationFormState | null = isDevelopment
    ? {
        donor_first_name: 'Important',
        donor_last_name: 'Tester',
        donor_email: 'action-tool@example.com',
        donor_phone: '1234567890',
        donor_address1: '123 Democracy Way',
        donor_city: 'Test City',
        donor_state: 'NY',
        donor_zip: '10001',
        donor_country_code: 'US',
        amount: '25',
        memo: 'Action Tool demo donation',
        cc_number: '4111 1111 1111 1111',
        cc_verification_value: '123',
        cc_month: '12',
        cc_year: '2030',
        cc_zip: '14600',
        cc_first_name: 'Important',
        cc_last_name: 'Tester'
      }
    : null

  const { allSanityPage } = useStaticQuery<{
    allSanityPage: Queries.SanityPageConnection
  }>(graphql`
    query DemocracyEngineDonationTerms {
      allSanityPage {
        nodes {
          slug {
            current
          }
          h1
          h2
          _rawBody(resolveReferences: { maxDepth: 4 })
        }
      }
    }
  `)

  const termsPage =
    allSanityPage?.nodes?.find(page => page?.slug?.current === 'democracy-engine-donation-details') ||
    undefined

  const updateState = (key: keyof DonationFormState) => (value: string) => {
    setState(prev => ({ ...prev, [key]: value }))
  }

  const amountValue = useMemo(() => Number(state.amount), [state.amount])

  const hasRequiredFields =
    Boolean(recipientId) &&
    amountValue > 0 &&
    state.donor_first_name.length > 0 &&
    state.donor_last_name.length > 0 &&
    state.donor_email.length > 0 &&
    state.donor_address1.length > 0 &&
    state.donor_city.length > 0 &&
    state.donor_state.length === 2 &&
    state.donor_zip.length > 0 &&
    state.cc_number.replace(/\s/g, '').length >= 13 &&
    state.cc_verification_value.length >= 3 &&
    state.cc_month.length === 2 &&
    state.cc_year.length === 4

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!hasRequiredFields) {
      return
    }
    setDismissedError(false)

    const donation: DemocracyEngineDonationInput = {
      donor_first_name: state.donor_first_name,
      donor_last_name: state.donor_last_name,
      donor_email: state.donor_email,
      donor_address1: state.donor_address1,
      donor_city: state.donor_city,
      donor_state: state.donor_state,
      donor_zip: state.donor_zip,
      donor_country_code: state.donor_country_code || 'US',
      donor_phone: state.donor_phone || undefined,
      payment_method: 'credit_card',
      cc_number: state.cc_number.replace(/\s/g, ''),
      cc_verification_value: state.cc_verification_value,
      cc_month: state.cc_month,
      cc_year: state.cc_year,
      cc_zip: state.cc_zip || state.donor_zip,
      cc_first_name: state.cc_first_name || state.donor_first_name,
      cc_last_name: state.cc_last_name || state.donor_last_name,
      line_items: [
        {
          recipient_id: recipientId,
          amount: amountValue.toFixed(2),
          memo: state.memo || undefined
        }
      ],
    }

    try {
      await submitBrowserDonation(donation)
      setSuccessMessage('Your donation request was submitted to Democracy Engine!')
      setState(initialState)
    } catch {
      setFailureMessage('Your donation could not be submitted. Please try again.')
    }
  }

  return (
    <VBox gap={2} sx={{ pointerEvents: 'all' }}>
      <Typography variant="h4" color="textPrimary" textAlign="center">
        Donate to <br /> {organization?.title || 'this organization'}
      </Typography>
      <Typography variant="caption" color="textPrimary" textAlign="center">
        Powered by Democracy Engine
      </Typography>
      <WhiteBox p={2} component="form" onSubmit={handleSubmit}>
        <LoadingOverlay loading={submitting} />
        {isDevelopment && !successMessage && demoDefaults && (
          <PrimaryButton
            type="button"
            variant="outlined"
            color="primary"
            size="small"
            sx={{ mb: 2, px: 2, py: 0.5 }}
            onClick={() => setState(demoDefaults)}
          >
            Fill test data (dev only)
          </PrimaryButton>
        )}
        {successMessage ? (
          <SuccessState message={successMessage} onClose={onClose} />
        ) : failureMessage || (!dismissedError && (submissionError || loadError)) ? (
          <FailureState
            message={failureMessage || 'Your donation could not be submitted. Please try again.'}
            onClose={() => {
              setFailureMessage(undefined)
              setDismissedError(true)
            }}
          />
        ) : (
          <VBox gap={1.5}>
            <Typography variant="overline" color="textPrimary">
              Donation details
            </Typography>
            <InputRow
              label="Amount (USD)"
              type="number"
              value={state.amount}
              onChange={value => updateState('amount')(value)}
              required
            />
            <Typography variant="overline" color="textPrimary">
              Donor information
            </Typography>
            <HBox gap={1} flexWrap="wrap">
              <InputRow
                label="First Name"
                value={state.donor_first_name}
                onChange={updateState('donor_first_name')}
                required
              />
              <InputRow
                label="Last Name"
                value={state.donor_last_name}
                onChange={updateState('donor_last_name')}
                required
              />
            </HBox>
            <InputRow
              label="Email"
              type="email"
              value={state.donor_email}
              onChange={updateState('donor_email')}
              required
            />
            <InputRow
              label="Address"
              value={state.donor_address1}
              onChange={updateState('donor_address1')}
              required
            />
            <HBox gap={1} flexWrap="wrap">
              <InputRow
                label="City"
                value={state.donor_city}
                onChange={updateState('donor_city')}
                required
              />
              <InputRow
                label="State (e.g. NY)"
                value={state.donor_state}
                onChange={value => updateState('donor_state')(value.toUpperCase())}
                required
              />
              <InputRow
                label="Zip"
                value={state.donor_zip}
                onChange={updateState('donor_zip')}
                required
              />
            </HBox>
            <InputRow
              label="Phone (optional)"
              value={state.donor_phone}
              onChange={updateState('donor_phone')}
              type="tel"
              required={false}
            />
            <Typography variant="overline" color="textPrimary">
              Payment details
            </Typography>
            <InputRow
              label="Card Number"
              value={state.cc_number}
              onChange={updateState('cc_number')}
              required
            />
            <HBox gap={1} flexWrap="wrap">
              <InputRow
                label="Exp. Month (MM)"
                value={state.cc_month}
                onChange={value => updateState('cc_month')(value.slice(0, 2))}
                required
                maxLength={2}
              />
              <InputRow
                label="Exp. Year (YYYY)"
                value={state.cc_year}
                onChange={value => updateState('cc_year')(value.slice(0, 4))}
                required
                maxLength={4}
              />
              <InputRow
                label="CVV"
                value={state.cc_verification_value}
                onChange={updateState('cc_verification_value')}
                required
                maxLength={4}
              />
            </HBox>
            <HBox gap={1} flexWrap="wrap">
              <InputRow
                label="Cardholder First Name (optional)"
                value={state.cc_first_name}
                onChange={updateState('cc_first_name')}
                required={false}
              />
              <InputRow
                label="Cardholder Last Name (optional)"
                value={state.cc_last_name}
                onChange={updateState('cc_last_name')}
                required={false}
              />
            </HBox>
            <InputRow
              label="Billing Zip (optional)"
              value={state.cc_zip}
              onChange={updateState('cc_zip')}
              required={false}
            />
            <InputRow
              label="Optional memo"
              value={state.memo}
              onChange={updateState('memo')}
              required={false}
            />
            <Typography variant="caption" color="textPrimary">
              By submitting, you agree to our{' '}
              <Link
                component="button"
                type="button"
                color="#fff"
                underline="always"
                onClick={() => setShowTermsModal(true)}
              >
                Terms & Conditions
              </Link>
              .
            </Typography>
            <Button
              variant="contained"
              disableElevation
              color={'primary'}
              sx={{ flex: 1, mt: 1 }}
              disabled={!hasRequiredFields || submitting}
              type={'submit'}
            >
              Submit Donation
            </Button>
          </VBox>
        )}
      </WhiteBox>
      <CenteredModal open={showTermsModal} onClose={() => setShowTermsModal(false)}>
        <Box>
          <Typography variant="h4" color="textPrimary">
            {termsPage?.h1 || 'Donation Terms'}
          </Typography>
          {termsPage?.h2 && (
            <Typography variant="body1" color="textPrimary" mt={1}>
              {termsPage.h2}
            </Typography>
          )}
          <Box mt={3}>
            {termsPage?._rawBody ? (
              // @ts-expect-error: existing type issue
              <PortableText value={termsPage._rawBody} components={blockSerializers} />
            ) : (
              <Typography variant="body1" color="textPrimary">
                Terms are not available right now. Please check back later.
              </Typography>
            )}
          </Box>
          <HBox justifyContent="flex-end" mt={3}>
            <PrimaryButton onClick={() => setShowTermsModal(false)}>Close</PrimaryButton>
          </HBox>
        </Box>
      </CenteredModal>
    </VBox>
  )
}

const InputRow = ({
  label,
  value,
  onChange,
  type = 'text',
  required = true,
  maxLength
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
  maxLength?: number
}) => {
  const id = `democracy-engine-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <InputComponent hasValue={Boolean(value)} onCloseClick={() => onChange('')}>
      {({ TextBox }) => (
        <TextBox
          id={id}
          placeholder={label}
          type={type}
          required={required}
          value={value}
          onChange={event => onChange(event.target.value)}
          inputProps={maxLength ? { maxLength } : undefined}
        />
      )}
    </InputComponent>
  )
}

const SuccessState = ({ message, onClose }: { message: string; onClose: () => void }) => (
  <VBox gap={2} alignItems="center" textAlign="center">
    <CheckmarkIcon size={72} />
    <Typography variant="h5" color="textPrimary">
      Thank you!
    </Typography>
    <Typography variant="body1" color="textPrimary">
      {message}
    </Typography>
    <PrimaryButton onClick={onClose}>Close</PrimaryButton>
  </VBox>
)

const FailureState = ({ message, onClose }: { message: string; onClose: () => void }) => (
  <VBox gap={2} alignItems="center" textAlign="center">
    <Typography variant="h5" color="textPrimary">
      Something went wrong
    </Typography>
    <Typography variant="body1" color="textPrimary">
      {message}
    </Typography>
    <HBox gap={1}>
      <PrimaryButton onClick={onClose}>Try again</PrimaryButton>
    </HBox>
  </VBox>
)
